// Firebase helper functions for common operations

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from './config';

/**
 * User Operations
 */

// Get a single user by ID
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

// Get all users
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

// Create or update a user
export const saveUserData = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      ...userData,
      updatedAt: Timestamp.now()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error saving user:', error);
    throw error;
  }
};

// Update user data
export const updateUserData = async (userId, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Get user by invitation code
export const getUserByInvitationCode = async (invitationCode) => {
  try {
    if (!invitationCode || invitationCode.trim() === '') {
      return null;
    }
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('invitationCode', '==', invitationCode.toUpperCase().trim()));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by invitation code:', error);
    throw error;
  }
};

// Get user by username
export const getUserByUsername = async (username) => {
  try {
    if (!username || username.trim() === '') {
      return null;
    }
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('username', '==', username.trim()));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by username:', error);
    throw error;
  }
};

/**
 * Admin Operations
 */

// Get all admin users
export const getAllAdmins = async () => {
  try {
    const adminsRef = collection(db, 'adminUsers');
    const querySnapshot = await getDocs(adminsRef);
    
    const admins = [];
    querySnapshot.forEach((doc) => {
      admins.push({ id: doc.id, ...doc.data() });
    });
    
    return admins;
  } catch (error) {
    console.error('Error getting admins:', error);
    throw error;
  }
};

// Check if user is admin
// Checks both adminUsers collection and users collection for admin roles
export const checkAdminStatus = async (userId, userEmail = null) => {
  const baseResult = {
    isAdmin: false,
    requiresUidFix: false,
    existingAdminId: null,
    message: '',
    source: null
  };

  try {
    console.log("🔍 Checking admin status for:", userId);
    
    // First, try to find admin by UID in adminUsers collection
    try {
      const adminRef = doc(db, 'adminUsers', userId);
      const adminSnap = await getDoc(adminRef);
      
      if (adminSnap.exists()) {
        console.log("✅ User found in adminUsers collection - ADMIN ACCESS GRANTED");
        return { ...baseResult, isAdmin: true, source: 'adminUsers' };
      }
    } catch (error) {
      console.log("❌ Error checking adminUsers collection:", error);
    }
    
    // Then check in users collection for admin role
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.role === 'superAdmin' || userData.role === 'admin' || userData.admin === true) {
          console.log("✅ User has admin role in users collection - ADMIN ACCESS GRANTED");
          return { ...baseResult, isAdmin: true, source: 'users' };
        }
      }
    } catch (error) {
      console.log("❌ Error checking users collection:", error);
    }
    
    // If not found by UID and email is provided, check by email in adminUsers
    if (userEmail) {
      try {
        const adminsRef = collection(db, 'adminUsers');
        const emailQuery = query(adminsRef, where('email', '==', userEmail.toLowerCase().trim()));
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          const mismatchDoc = emailSnapshot.docs[0];
          const message = 'Admin record found, but its Firestore document ID does not match your UID. Follow the steps in DEBUG_ADMIN.md (Step 2A) to recreate the document using your UID.';
          console.warn(`Admin found by email but UID mismatch. Document ID: ${mismatchDoc.id}, User UID: ${userId}`);
          
          return {
            ...baseResult,
            requiresUidFix: true,
            existingAdminId: mismatchDoc.id,
            message
          };
        }
      } catch (error) {
        console.log("❌ Error checking adminUsers by email:", error);
      }
    }
    
    console.log("❌ User is not admin");
    return baseResult;
  } catch (error) {
    console.error("🔥 Admin check error:", error);
    if (error?.code === 'permission-denied') {
      return {
        ...baseResult,
        requiresUidFix: true,
        message: 'Firestore denied access to admin records. This usually means your admin document does not exist under your UID. Follow DEBUG_ADMIN.md Step 2A to recreate it.'
      };
    }
    return { ...baseResult, message: error.message || 'Unknown admin lookup error' };
  }
};

// Add user as admin (for initial setup)
export const addAdminUser = async (userId, email) => {
  try {
    const adminRef = doc(db, 'adminUsers', userId);
    await setDoc(adminRef, {
      email: email || '',
      createdAt: Timestamp.now(),
      addedBy: auth?.currentUser?.uid || 'system'
    });
    console.log(`✅ User ${userId} added as admin`);
    return true;
  } catch (error) {
    console.error('Error adding admin user:', error);
    throw error;
  }
};

/**
 * Activity Log Operations
 */

// Log admin activity
export const logAdminActivity = async (activityData) => {
  try {
    const activityRef = doc(collection(db, 'adminActivityLog'));
    await setDoc(activityRef, {
      ...activityData,
      timestamp: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Error logging activity:', error);
    throw error;
  }
};

// Get activity logs
export const getActivityLogs = async (filters = {}) => {
  try {
    let q = query(collection(db, 'adminActivityLog'), orderBy('timestamp', 'desc'));
    
    if (filters.adminId) {
      q = query(q, where('adminId', '==', filters.adminId));
    }
    
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const querySnapshot = await getDocs(q);
    const logs = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    
    return logs;
  } catch (error) {
    console.error('Error getting activity logs:', error);
    throw error;
  }
};

/**
 * Test Connection
 */
export const testFirebaseConnection = async () => {
  try {
    // Just verify Firebase is initialized (no authentication needed)
    if (db) {
      console.log('✅ Firebase connected successfully!');
      console.log('📊 Firestore database is initialized and ready');
      console.log('💡 Note: Reading data requires authentication');
      return true;
    } else {
      console.error('❌ Firebase not initialized');
      return false;
    }
  } catch (error) {
    console.error('❌ Firebase connection error:', error);
    return false;
  }
};

